import { sortNumericRange } from "@decentriq/utils";
import { faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Checkbox,
  Chip,
  ChipDelete,
  type ChipDeleteProps,
  IconButton,
  Option,
  type OptionProps,
  Select,
  type SelectProps,
  Stack,
  Tooltip,
} from "@mui/joy";
import capitalize from "lodash/capitalize";
import { memo, useCallback, useMemo } from "react";
import {
  type AudienceAttributes,
  AudienceAttributeValueType,
  RuleBasedAudienceCondition,
  type RuleBasedAudienceFilter,
} from "features/mediaDataRoom/models";
import { mapAudienceConditionToPresentation } from "../../config";

interface AudienceFilterProps {
  attributes: AudienceAttributes;
  onChange: (payload: RuleBasedAudienceFilter) => void;
  handleRemove: () => void;
  attribute: string | null;
  condition: RuleBasedAudienceCondition | null;
  values: string[] | null;
}

const SELECT_ALL_OPTION_VALUE = "Select all";

const AudienceFilter = memo<AudienceFilterProps>(
  ({ onChange, attributes, handleRemove, attribute, condition, values }) => {
    const handleAttributeChange = useCallback<
      NonNullable<SelectProps<string, false>["onChange"]>
    >(
      (_, value) => {
        onChange({
          attribute: value as string,
          condition: null,
          values: null,
        });
      },
      [onChange]
    );
    const handleConditionChange = useCallback<
      Required<
        Pick<SelectProps<RuleBasedAudienceCondition, false>, "onChange">
      >["onChange"]
    >(
      (_, value) => {
        onChange({
          attribute,
          condition: value,
          values: null,
        });
      },
      [attribute, onChange]
    );
    const handleValueChange = useCallback<
      NonNullable<SelectProps<string, true>["onChange"]>
    >(
      (_, values) => {
        if (values.includes(SELECT_ALL_OPTION_VALUE)) {
          return;
        }
        onChange({
          attribute,
          condition,
          values,
        });
      },
      [attribute, condition, onChange]
    );
    const handleValueDelete = useCallback<
      (value: string) => NonNullable<ChipDeleteProps["onDelete"]>
    >(
      (value) => (event) => {
        event.stopPropagation();
        onChange({
          attribute,
          condition,
          values: values?.filter((v) => v !== value) ?? [],
        });
      },
      [onChange, attribute, condition, values]
    );
    const generatedAttributes = useMemo(
      () => Object.keys(attributes),
      [attributes]
    );
    const generatedValues = useMemo(() => {
      if (!attribute) {
        return [];
      }
      let { values, valueType } = attributes[attribute];
      if (valueType === AudienceAttributeValueType.NUMERIC_RANGE) {
        values = sortNumericRange(values);
      }
      return values.map((value) => ({
        label: value.trim().length ? value : "(empty value)",
        value,
      }));
    }, [attribute, attributes]);
    const isAllSelected = useMemo(
      () => values?.length === generatedValues.length,
      [values, generatedValues]
    );
    const handleAllValuesSelect = useCallback<
      NonNullable<OptionProps["onClick"]>
    >(() => {
      onChange({
        attribute,
        condition,
        values: isAllSelected ? [] : generatedValues.map(({ value }) => value),
      });
    }, [isAllSelected, onChange, attribute, condition, generatedValues]);
    const generatedFilters = useMemo<
      { value: RuleBasedAudienceCondition; label: string }[]
    >(() => {
      if (!attribute) {
        return [];
      }
      return attributes[attribute].filters.map((value) => ({
        label: mapAudienceConditionToPresentation[value] ?? value,
        value,
      }));
    }, [attribute, attributes]);
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{ "& > *": { flex: 1 }, pl: 4, width: "100%" }}
      >
        <Tooltip
          placement="top"
          title={
            generatedAttributes.length !== 0
              ? ""
              : "To select an attribute, choose an audience first"
          }
        >
          <span>
            <Select
              disabled={generatedAttributes.length === 0}
              onChange={handleAttributeChange}
              placeholder="Add attribute"
              value={attribute}
            >
              {generatedAttributes.map((attribute) => (
                <Option key={attribute} value={attribute}>
                  {capitalize(attribute)}
                </Option>
              ))}
            </Select>
          </span>
        </Tooltip>
        <Tooltip
          placement="top"
          title={
            attribute ? "" : "To select a condition, add an attribute first"
          }
        >
          <span>
            <Select
              disabled={!attribute}
              onChange={handleConditionChange}
              placeholder="Add condition"
              value={condition}
            >
              {generatedFilters.map(({ label, value }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </span>
        </Tooltip>
        <Select
          disabled={
            !attribute ||
            condition === RuleBasedAudienceCondition.EMPTY ||
            condition === RuleBasedAudienceCondition.NOT_EMPTY
          }
          multiple={true}
          onChange={handleValueChange}
          placeholder="Add values"
          renderValue={(selected) =>
            selected.map(({ value, label }) => (
              <Chip
                endDecorator={
                  <ChipDelete onDelete={handleValueDelete(value)} />
                }
                key={value}
                sx={{ mr: 1 }}
              >
                {label}
              </Chip>
            ))
          }
          sx={{
            visibility:
              condition &&
              condition !== RuleBasedAudienceCondition.EMPTY &&
              condition !== RuleBasedAudienceCondition.NOT_EMPTY
                ? "visible"
                : "hidden",
          }}
          value={values ?? []}
        >
          <Option
            label={SELECT_ALL_OPTION_VALUE}
            onClick={handleAllValuesSelect}
            value={SELECT_ALL_OPTION_VALUE}
          >
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                (values?.length ?? 0) > 0 &&
                (values?.length ?? 0) < generatedValues.length
              }
              label={SELECT_ALL_OPTION_VALUE}
            />
          </Option>
          {generatedValues.map(({ label, value }) => (
            <Option key={value} label={label} value={value}>
              <Checkbox
                checked={values?.includes(value) ?? false}
                label={label}
              />
            </Option>
          ))}
        </Select>
        <Tooltip title="Remove filter">
          <IconButton
            onClick={handleRemove}
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
              },
              flex: "unset",
            }}
          >
            <FontAwesomeIcon fixedWidth={true} icon={faTrash} />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }
);

AudienceFilter.displayName = "AudienceFilter";

export default AudienceFilter;
